import React, { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

const BannerModal = ({ isOpen, banner, products, onClose, onSave, onUploadImage }) => {
  const [form, setForm] = useState({
    title: 'Banner',
    subtitle: '',
    button_text: '',
    button_link: '',
    product_id: '',
    active: true,
    order: 0
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (banner) {
      setForm({
        title: banner.title || 'Banner',
        subtitle: banner.subtitle || '',
        button_text: banner.button_text || '',
        button_link: banner.button_link || '',
        product_id: banner.product_id || '',
        active: banner.active !== false,
        order: banner.order || 0
      });
      setImagePreview(banner.image_url);
    } else {
      setForm({
        title: 'Banner',
        subtitle: '',
        button_text: '',
        button_link: '',
        product_id: '',
        active: true,
        order: 0
      });
      setImagePreview(null);
    }
  }, [banner]);

  const handleSubmit = async () => {
    await onSave(form);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !banner?.id) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      await onUploadImage(banner.id, file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{banner ? 'Editar Banner' : 'Novo Banner'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Image Upload - Only for existing banners */}
          {banner?.id && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Imagem do Banner</label>
              <div className="relative aspect-[3/1] bg-slate-100 rounded-xl overflow-hidden group border-2 border-dashed border-slate-300">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <span className="text-sm">Clique para carregar imagem</span>
                  </div>
                )}
                <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <div className="text-white flex items-center gap-2">
                    {uploading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        <span>{imagePreview ? 'Trocar Imagem' : 'Carregar Imagem'}</span>
                      </>
                    )}
                  </div>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Recomendado: Imagem horizontal (ex: 1200x400 pixels)
              </p>
            </div>
          )}

          {/* Simple fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Ordem de exibição</label>
              <Input 
                type="number"
                value={form.order}
                onChange={(e) => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                placeholder="0"
              />
            </div>
            <div className="flex items-end pb-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="banner_active"
                  checked={form.active}
                  onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <label htmlFor="banner_active" className="text-sm font-medium text-slate-700">
                  Banner ativo
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700 text-white">
              {banner ? 'Salvar' : 'Criar Banner'}
            </Button>
          </div>

          {!banner && (
            <p className="text-sm text-slate-500 text-center bg-blue-50 p-3 rounded-lg">
              Após criar, você poderá carregar a imagem do banner
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BannerModal;
